import axios from "axios";

// Canonical Authorization scheme prefix. The HTTP scheme is case-insensitive,
// but a single casing keeps the placeholder comparisons below reliable.
export const BEARER_PREFIX = "Bearer ";

// Authorization values that look set but carry no real token yet.
const BEARER_PLACEHOLDERS = [BEARER_PREFIX, `${BEARER_PREFIX}Unknown`];

// Treat a token as expired this long before its real expiry, as a safety margin.
export const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

export interface BackoffConfig {
    baseDelay: number;
    maxDelay: number;
    jitterPercent: number;
}

// The API base URL already includes the `/api` segment, so strip a leading
// `/api` or `api` from request URLs to avoid duplicating it.
export function stripApiPrefix(url: string | undefined): string | undefined {
    if (url?.startsWith("/api")) return url.slice(4);
    if (url?.startsWith("api")) return url.slice(3);
    return url;
}

// True when the request already carries a usable bearer token (not a placeholder).
export function hasValidAuthHeader(authorization: unknown): boolean {
    return typeof authorization === "string"
        && authorization.length > 0
        && !BEARER_PLACEHOLDERS.includes(authorization);
}

// Exponential backoff with jitter: min(base * 2^attempt, max), plus random jitter.
export function computeBackoffDelay(attempt: number, config: BackoffConfig): number {
    const exponentialDelay = Math.min(config.baseDelay * Math.pow(2, attempt), config.maxDelay);
    const jitter = Math.random() * config.jitterPercent * exponentialDelay;
    return exponentialDelay + jitter;
}

// Fires the server-side group-claims sync. Never throws: a failure here must not
// block authentication, so callers can fire-and-forget.
export async function syncGroupClaims(): Promise<any> {
    try {
        const response = await axios.post("/User/sync-group-claims");
        return response.data;
    } catch (error) {
        console.warn("Group claims sync failed:", error);
        return { success: false, error };
    }
}
